import { action, computed, observable } from 'mobx';
import { AxiosResponse } from 'axios';
import { drawRectangularBase, drawCircleBase, updateRectangularGraphic, removeLayers, getBoundaryPolygon, isPointInsidePolygon } from '../../../utils/gisUtils';
import {
  SimpleMarkerSymbol,
  Point,
  GraphicsLayer,
  Graphic,
  esriProjection,
  SpatialReference,
  MapView,
  Sketch,
  SceneView,
  FeatureLayer,
  RasterData,
  FeatureSet,
  GroupLayer,
  esriGeometryEngine,
  ImageryLayer,
  Geometry,
} from 'cd-eft/utils/esriTypes';
import Layer from '@arcgis/core/layers/Layer';
import stores from '../../../models/Stores';
import Home from '@arcgis/core/widgets/Home';
import { ICreateProjectPOST } from 'cd-eft/models/Projects/createProject';
import { IpostGetElevationData } from 'cd-eft/models/SurveyEngineering/getElevation';
import { ToolSlot } from '../BreaklineTools/framework/Tool';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

import sleep from 'cd-eft/utils/sleep';
import { UserIdentity } from 'cd-eft/security/UserIdentity';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { ProjectDataObject } from 'cd-eft/models/Projects/projectsLibrary';

import { LinearUnit, turnOffLabelLayer } from 'cd-eft/utils/structureLayerUtils';
import { TinContourSurface } from 'cd-eft/framework/TinContourSurface/TinContourSurface';
import { appConfig } from 'cd-eft/config/AppConfig';
import { AppConstants } from 'cd-eft/appConstants';
import { DesignElementResponse } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import FeatureSnappingLayerSource from '@arcgis/core/views/interactive/snapping/FeatureSnappingLayerSource';
import { SnapTooltipProvider } from '../../../framework/SnapTooltipProvider/SnapTooltipProvider';
import { getSpatialRefByProject } from 'cd-eft/components/Projects/ProjectsLibrary/ProjectsLibraryVM'
import DemSurfaceGpApi from 'cd-eft/services/DemSurfaceGpApi/DemSurfaceGpApi';
import { zoomToSubjectArea } from 'cd-eft/utils/gisUtils';
import { doEvents } from 'cd-eft/utils/sysUtils';
import { Screenshot, ILayerList } from '../../common/Screenshot/Screenshot';
import { ScreenshotPreviewVM } from 'cd-eft/components/common/Screenshot/ScreenshotPreviewVM';

const { projectsLibraryStore, pitDesignStore, padDesignStore, designStore, userProfileStore } = stores.AppStore;
const __notAvailableSuffix: string = ' NOT AVAILABLE';

export class DesignContainerVM {
  @observable snapMessage: string = "Snap to Point";
  @observable static screenshotLoading: boolean = false;
  @observable view?: MapView;
  @observable sceneView?: SceneView;
  @observable sketchWidget?: Sketch;
  @observable bpSketchWidget?: Sketch;
  @observable result_dem: RasterData | undefined;
  @observable result_dem_boundary: FeatureSet | undefined;
  @observable in_dem_file: File | undefined = undefined;
  @observable in_dem_elevation_units: string = 'feet';
  public readonly aoiGraphicsLayer: GraphicsLayer;
  public readonly aoiSketch: Sketch;
  @observable jobExecuting: boolean = false;
  public readonly sketchGroupLayer: GroupLayer;
  public readonly resultsGroupLayer: GroupLayer;
  @observable crossSectionOpen: boolean = false;
  @observable dataSourceType: string = '';
  @observable metadata: string = '';
  @observable gpsessionResourceTypeId: number = 1;
  @observable private readonly api: DemSurfaceGpApi = new DemSurfaceGpApi();
  homeWidget?: Home;
  private drawingActive = false;
  private surveyPointsLayer: GraphicsLayer | undefined;

  @observable countyExtent?: Geometry;

  // @observable notification: any = { message: '', header: '', type: 'warning', hidden: true };
  private centerPointSketch?: Sketch;
  private readonly centerPointLayer = new GraphicsLayer({ id: 'centerPointGraphic', title: 'Center Point', listMode: 'hide' });
  @observable pitLayers: Layer[];
  // private allGroundPoints: Multipoint = new Multipoint();
  history;
  @observable isWDTCollapsed: boolean = true;
  @observable padLayers: Layer[];
  @observable dikedPitLayers: Layer[];
  private pendingEditErrorMessages: string[] = ['Common_15'];
  private _currentWkid = appConfig.tempHardCodes.wkid;

  readonly toolSlot: ToolSlot;
  // public _snapTooltipProvider: SnapTooltipProvider | undefined = void 0;
  public _snapTooltipProvider: SnapTooltipProvider | undefined = void 0;
  public workingSpatialReference?: SpatialReference = undefined;

  public static readonly origGpSurfaceTypeId = 5;

  private static instance: DesignContainerVM;

  originalSurface?: TinContourSurface;
  sourceWkid = appConfig.tempHardCodes.wkid;
  sourceElevUnit: LinearUnit = 'intl-feet';
  mapElevUnit: LinearUnit = 'meters';

  public readonly demServicesGroupLayer: GroupLayer;

  childCallables;
  setChildCallables = (callables) => {
    this.childCallables = callables;
  }
  toggleChildSnapping() {
    if (this.childCallables) this.childCallables.toggleSnapping();
  }

  constructor(history) {
    this.pitLayers = [];
    this.history = history;
    this.padLayers = [];
    this.dikedPitLayers = [];
    this.toolSlot = new ToolSlot();
    this.centerPointSketch = new Sketch({
      layer: this.centerPointLayer,
      creationMode: 'single',
      snappingOptions: {
        distance: 21,
      },
      visibleElements: {
        createTools: {
          point: false,
          polyline: false,
          polygon: false,
          rectangle: false,
          circle: false,
        },
        selectionTools: {
          'rectangle-selection': false,
          'lasso-selection': false,
        },
        settingsMenu: false,
        undoRedoMenu: false,
      } as any,
      defaultUpdateOptions: {
        enableScaling: false,
        enableRotation: false,
        toggleToolOnClick: false,
        preserveAspectRatio: false,
      }
    });

    this.centerPointSketch.on('create', async (event) => {
      if (event.state === 'complete') {
        if (this._snapTooltipProvider) {
          this._snapTooltipProvider.stop();
          this._snapTooltipProvider = undefined;
        }
        const point = event.graphic.geometry as Point;
        const projectedPoint: any = esriProjection.project(point, projectsLibraryStore.projectSpatialReference!);
        await this.getCenterPoint(projectedPoint);
        this.redefineSketch();
      } else if (event.state === "cancel") {
        if (this._snapTooltipProvider) {
          this._snapTooltipProvider.stop();
          this._snapTooltipProvider = undefined;
        }
        this.redefineSketch();
        pitDesignStore.chooseCenter = false;
        padDesignStore.chooseCenter = false;
      }
    });

    this.aoiGraphicsLayer = new GraphicsLayer({
      id: 'aoiGraphicsLayer',
      title: 'AOIGraphics',
      visible: true,
    });

    this.sketchGroupLayer = new GroupLayer({
      id: 'sketchGroupLayer',
      title: 'Design - Input',
      listMode: 'hide',
      layers: [
        this.aoiGraphicsLayer,
      ],
    });

    this.resultsGroupLayer = new GroupLayer({
      id: 'resultsGroupLayer',
      title: 'Design',
      layers: [],
    });

    this.demServicesGroupLayer = new GroupLayer({
      id: 'demServicesGroupLayer',
      title: 'DEM Services',
      layers: [],
    });

    this.aoiSketch = new Sketch({
      id: 'aoiSketch',
      layer: undefined as any,
      view: undefined as any,
      creationMode: 'single',
      visibleElements: {
        createTools: {
          point: false,
          polyline: false,
          polygon: true,
          rectangle: true,
          circle: true,
        },
        selectionTools: {
          'rectangle-selection': true,
          'lasso-selection': true,
        },
        settingsMenu: false,
        undoRedoMenu: false,
      } as any,
    });

    DesignContainerVM.instance = this;
  }

  @observable routeProjectId;
  @action.bound setProjectId = (projectId) => (this.routeProjectId = projectId);

  @action.bound toggleSideNav() {
    // e.preventDefault();
    this.isWDTCollapsed = !this.isWDTCollapsed;
  }

  //pass home button into VM
  @action.bound async onMapViewMounted(mapView: MapView) {
    designStore.isLoading = true;
    await this.initializeDesignFlow();

    this.view = mapView;
    //wait until the mapView is finished loading the initial map contents
    await mapView.when();

    // Call the function to zoom to the desired state and county
    await zoomToSubjectArea(mapView, DesignContainerVM, designStore);

    // initialize ability to click map points
    // this.setUpSelectPointOnMap();
    this.centerPointSketch!.view = this.view;
    this.view.map.add(this.centerPointLayer);

    await this._loadOriginalSurface(false);
  }
  @action.bound async onSceneViewMounted(sceneView: SceneView) {
    this.sceneView = sceneView;
    //wait until the sceneView is finished loading the initial map contents
    await sceneView.when();
  }

  @computed static get mapView(): MapView | undefined {
    return (this.instance ? this.instance.view : undefined);
  }

  public static setTinVisibility(visible: boolean) {
    if (this.instance) {
      this.instance._setTinVisibility(visible);
    }
  }

  public static async loadOriginalSurface(waitForScene: boolean = false): Promise<boolean> {
    let result: boolean = false;
    if (this.instance) {
      result = await this.instance._loadOriginalSurface(waitForScene);
    } else {
      MessageCenter.addError('No DesignContainerVM instance found.');
    }
    return result;
  }

  private _setTinVisibility(visible: boolean) {
    if (this.view && this.view.map) {
      if (!visible) {
        for (const id of [AppConstants.OriginalTinTrianglesLayerId, AppConstants.DesignedTinTrianglesLayerId]) {
          const tinLayer = this.view.map.findLayerById(id);
          if (tinLayer) {
            tinLayer.visible = visible;
          }
        }
      } else {
        const originalTinTrianglesLayer = this.view.map.findLayerById(AppConstants.OriginalTinTrianglesLayerId);
        const designedTinTrianglesLayer = this.view.map.findLayerById(AppConstants.DesignedTinTrianglesLayerId);
        const designedContourLayer = this.view.map.findLayerById(AppConstants.DesignedContourLayerId);
        let designedSurface: boolean = false;
        if (designedContourLayer) designedSurface = designedContourLayer.visible;
        if (originalTinTrianglesLayer) originalTinTrianglesLayer.visible = !designedSurface;
        if (designedTinTrianglesLayer) designedTinTrianglesLayer.visible = designedSurface;
      }
    }
  }

  public static async takeScreenshot(sceneViewActive: boolean, screeenshotPreview: ScreenshotPreviewVM) {
    if (this.instance) {
      await this.instance._takeScreenshot(sceneViewActive, screeenshotPreview);
    } else {
      MessageCenter.addError('No DesignContainerVM instance found.');
    }
  }

  private async _takeScreenshot(sceneViewActive: boolean, screeenshotPreview: ScreenshotPreviewVM) {
    const screenshotLayerList = this._buildScreenshotLayerList(sceneViewActive);
    if ((screenshotLayerList === undefined) ||
      ((screenshotLayerList.surfaceId === undefined) && (screenshotLayerList!.layers === undefined))) {
      await MessageCenter.addErrorFromCode('DesignElement_9');
      return;
    }
    let screenshot: Screenshot;
    if (sceneViewActive) {
      if (!this.sceneView) return;
      screenshot = new Screenshot(
        this.sceneView,
        designStore.projectsStore.currentProjectId as string,
        designStore.projectsStore.currentSessionId as string,
        screeenshotPreview
      );
    } else {
      if (!this.view) return;
      screenshot = new Screenshot(
        this.view,
        designStore.projectsStore.currentProjectId as string,
        designStore.projectsStore.currentSessionId as string,
        screeenshotPreview
      );
    }
    //DesignContainerVM.screenshotLoading = true;
    await screenshot.takeScreenshot(screenshotLayerList);
  }

  private _buildScreenshotLayerList(sceneViewActive: boolean): ILayerList | undefined {
    let result: ILayerList | undefined;
    const map = (sceneViewActive ? this.sceneView : this.view)?.map;
    if (!map) {
      return result;
    }

    if ((designStore.designElementTypeId === 0) || (designStore.designElementTypeId === 2)) {
      if (designStore.currentSurface && designStore.currentSurface.designElementId) {
        result = {
          designElementTypeId: 2,
          surfaceId: designStore.currentSurface.designElementId
        };
      }
    } else if ((designStore.designElementTypeId === 4) || (designStore.designElementTypeId === 25)) {
      const layers = map.layers.toArray().filter(l => { return ((l.visible) && (l.id.startsWith('Terrace'))) });
      if (layers.length > 0) {
        result = {
          designElementTypeId: 4,
          layers: layers
        };
      }
    } else if (designStore.designElementTypeId === 5) {
      const layers = map.layers.toArray().filter(l => { return ((l.visible) && (l.id.startsWith('alignment'))) });
      if (layers.length > 0) {
        result = {
          designElementTypeId: 5,
          layers: layers
        };
      }
    } else if (designStore.designElementTypeId === 7) {
      const layers = map.layers.toArray().filter(l => { return ((l.visible) && (l.id.startsWith('Waterway'))) });
      if (layers.length > 0) {
        result = {
          designElementTypeId: 7,
          layers: layers
        };
      }
    } else if (designStore.designElementTypeId === 11) {
      const store = stores.AppStore.pitDesignStore;
      const layers: Layer[] = [];
      const typeIds: number[] = [];
      for (const pit of store.pitSummaryTable) {
        const layer = map.findLayerById(pit.designElementId.toString());
        if (layer && layer.visible) {
          layers.push(layer);
          typeIds.push(pit.designElementTypeId.id);
        }
      }
      if (layers.length > 0) {
        result = {
          designElementTypeId: 11,
          designElementTypeIds: typeIds,
          layers: layers
        };
      }
    } else if (designStore.designElementTypeId === 14) {
      const store = stores.AppStore.padDesignStore;
      const layers: Layer[] = [];
      for (const pad of store.padSummaryTable) {
        const layer = map.findLayerById(pad.designElementId.toString());
        if (layer && layer.visible) {
          layers.push(layer);
        }
      }
      if (layers.length > 0) {
        result = {
          designElementTypeId: 14,
          layers: layers
        };
      }
    } else if (designStore.designElementTypeId === 15) {
      const store = stores.AppStore.landLevelingDesignStore;
      const layers: Layer[] = [];
      for (const laddLeveling of store.landLevelingSummaryTable) {
        const layer = map.findLayerById(laddLeveling.designElementId.toString());
        if (layer && layer.visible) {
          layers.push(layer);
        }
      }
      if (layers.length > 0) {
        result = {
          designElementTypeId: 15,
          layers: layers
        };
      }
    }

    return result;
  }

  /**
   * useful when deleting surveys
   */
  private _cleanUpLayers() {
    const map = this.view?.map;
    if (map) {
      const designedContourLayer = map.findLayerById(AppConstants.DesignedContourLayerId) as FeatureLayer;
      if (designedContourLayer) {
        const removedLayer = map.remove(designedContourLayer);
        removedLayer.destroy();
      }

      const originalContourLayer = map.findLayerById(AppConstants.OriginalContourLayerId) as FeatureLayer;
      if (originalContourLayer) {
        const removedLayer = map.remove(originalContourLayer);
        removedLayer.destroy();
      }

      const originalTrianglesLayer = map.findLayerById(AppConstants.OriginalTinTrianglesLayerId) as GraphicsLayer;
      if (originalTrianglesLayer) {
        // originalTrianglesLayer.removeAll();
        const removedLayer = map.remove(originalTrianglesLayer);
        removedLayer.destroy();
      }

      const designedTrianglesLayer = map.findLayerById(AppConstants.DesignedTinTrianglesLayerId) as GraphicsLayer;
      if (designedTrianglesLayer) {
        // designedTrianglesLayer.removeAll();
        const removedLayer = map.remove(designedTrianglesLayer);
        removedLayer.destroy();
      }

      const surveyPointsLayer = map.findLayerById(AppConstants.SurveyPointsLayerId) as GraphicsLayer;
      if (surveyPointsLayer) {
        // surveyPointsLayer.removeAll();
        const removedLayer = map.remove(surveyPointsLayer);
        removedLayer.destroy();
      }
    }
  }

  private async _loadOriginalSurface(waitForScene: boolean): Promise<boolean> {
    let result: boolean = false;

    try {
      designStore.setLoader('Loading original surface...');
      doEvents();

      // needed when deleting surveys
      this._cleanUpLayers();
      if (this.view) {
        await this.restoreProjectState();
        //getDEMMetadata returns a boolean to indicate if it is a dem
        const temp = await this.getDEMMetadata();
        if (temp === undefined) {
          result = false;
        }
        else {
          result = temp;
        }
        if (result || designStore.currentSurface.surfaceDefClassName === 'dem') {
          try {
            //use getDEMMetadata to ensure the surface class is updated to dem
            designStore.currentSurface.surfaceDefClassName = 'dem';
            let demLayer: any;
            if (this.dataSourceType === 'User Defined') {
              const response: any = await PRJAPI.EftSessionController.getSessionResourcesBySessionUuidAndTypeIdUsingGET(
                designStore.projectsStore.currentSessionId, this.gpsessionResourceTypeId
              );
              designStore.gpServiceSessionID = response.data[0].sessionResourceValue;
              demLayer = await this.api.LoadDEMFromSession({
                input_server_folder: appConfig.gpSessionRoot,
                input_session: designStore.gpServiceSessionID
              });
              try {
                // Check if the GP server URL exists and is valid before trying to load
                if (demLayer.url && demLayer.url.includes("MapServer")) {
                  await demLayer.load(); // Attempt to load the layer
                }
              } catch (error) {
                MessageCenter.addWarningFromCode('DEM_83');
                console.log(error); //logs geoprocessing url.
              }

            } else if (this.dataSourceType === 'DEM Service') {
              demLayer = new ImageryLayer({
                title: 'Selected Dem Layer',
                url: this.metadata,
                id: 'selectedDemLayer',
                renderingRule: {
                  functionName: "Stretch",
                  functionArguments: {
                    Raster: "$$",
                    StretchType: 6,
                    MinPercent: 2,
                    MaxPercent: 2,
                    DRA: true,
                    Gamma: [1, 1, 1],
                    UseGamma: true,
                  },
                  outputPixelType: "u8",
                },
              });
            }

            let hillshadeLayer = this.view.map.findLayerById('usdaNedBestAvailable');

            //Add dem layer to the map
            if (demLayer) {
              const selectedDemById = this.view.map.findLayerById('selectedDemLayer') as ImageryLayer;
              if (selectedDemById) this.view.map.remove(selectedDemById);
              const selectedDemByTitle = this.view.map.allLayers.filter((layer) => {
                return layer.title === 'Selected Dem Layer';
              });
              if (selectedDemByTitle) {
                for (const lyr of selectedDemByTitle) {
                  this.view.map.remove(lyr);
                }
              }
              demLayer.title = "Selected Dem Layer";
              if (hillshadeLayer) {
                this.view.map.add(demLayer, 1);
                hillshadeLayer.visible = false;
              } else {
                this.view.map.add(demLayer, 0);
              }
              // Turnoff DEM layer as default
              demLayer.visible = false;
            }

            //turn on the best available hillshade layer if it is available after save DEM
            //id is usdaNedBestAvailable
            if (!hillshadeLayer) {
              for (let i = 0; i < 10; ++i) {
                try {
                  const configDemServices = appConfig.demMapServices;
                  // Find the element with id "usdaNedBestAvailable"
                  const targetService = configDemServices.find(service => service.id === "usdaNedBestAvailable");
                  hillshadeLayer = new ImageryLayer({
                    ...targetService,
                  });
                  await hillshadeLayer.load();
                  if (hillshadeLayer && !hillshadeLayer.title.endsWith(__notAvailableSuffix)) {
                    //Add hillshade layer to the map
                    this.view.map.remove(hillshadeLayer);
                    hillshadeLayer.visible = false;
                    this.view.map.add(hillshadeLayer, 0);
                    break;
                  }
                } catch (error) {
                  console.log(error);
                }
              }
            }
          } catch (error) {
            MessageCenter.addError((error as Error).message);
          }
        }
        if (!this.originalSurface) {
          this.originalSurface = new TinContourSurface(this.history, this.view, this.mapElevUnit, false);
        }

        const sessionUuid = designStore.projectsStore.currentSessionId;

        if (!('' === sessionUuid || undefined === sessionUuid)) {
          doEvents();
          const hasData = await this.originalSurface.retrieveOriginalSurface(
            sessionUuid,
            this.sourceWkid,
            this.sourceElevUnit
          );
          if (hasData) {
            doEvents();
            result = await this._displayOriginalSurface(waitForScene);
          }
        }
      } else {
        MessageCenter.addError('Map view does not currently exist.');
      }
    } catch (error) {
      console.log(error);

    } finally {
      doEvents();
      designStore.setLoader('');
    }
    return result;
  }

  public static async loadMultiStructureSurface(
    structureIds: number[],
    dataWkid: number | undefined = undefined,
    dataElevUnit: LinearUnit | undefined = undefined
  ): Promise<boolean> {
    let result: boolean = false;
    if (this.instance) {
      result = await this.instance._loadMultiStructureSurface(
        dataWkid || this.instance.sourceWkid,
        dataElevUnit || this.instance.sourceElevUnit,
        structureIds
      );
    } else {
      MessageCenter.addError('No DesignContainerVM instance found.');
    }
    return result;
  }

  private async _loadMultiStructureSurface(
    dataWkid: number,
    dataElevUnit: LinearUnit,
    structureIds: number[]
  ): Promise<boolean> {
    let result: boolean = false;
    try {
      designStore.setLoader('Loading multi-structure surface...');
      if (this.view) {
        let sessionUuid = designStore.projectsStore.currentSessionId;
        let counter = 0;
        while ('' === sessionUuid || undefined === sessionUuid) {
          await sleep(1000);
          sessionUuid = designStore.projectsStore.currentSessionId;
          counter++;
          if (15 < counter) {
            MessageCenter.addError('Session ID has never been defined.');
            break;
          }
        }
        // console.log('Session ID: ' + sessionUuid);

        if (!('' === sessionUuid || undefined === sessionUuid)) {
          const surface = new TinContourSurface(this.history, this.view, this.mapElevUnit, true);
          const hasData = await surface.retrieveMultiStructureSurface(sessionUuid, structureIds, dataWkid, dataElevUnit);
          if (hasData) {
            result = await surface.displaySurface(dataWkid, false);
          }
        }
      } else {
        MessageCenter.addError('Map view does not currently exist.');
      }
    } catch (error) {
      console.log(error);

    } finally {
      designStore.setLoader('');
    }
    return result;
  }

  public static async loadSurfaceFromData(
    tinTriangles: any[],
    cgPoints: any[] | undefined = undefined,
    contours: any[] | undefined = undefined,
    dataWkid: number | undefined = undefined,
    dataElevUnit: LinearUnit | undefined = undefined,
    ensureTin: boolean = false
  ): Promise<boolean> {
    let result: boolean = false;
    if (this.instance) {
      result = await this.instance._loadSurfaceFromData(
        dataWkid || this.instance.sourceWkid,
        dataElevUnit || this.instance.sourceElevUnit,
        tinTriangles,
        cgPoints,
        contours,
        ensureTin
      );
    } else {
      MessageCenter.addError('No DesignContainerVM instance found.');
    }
    return result;
  }

  private async _loadSurfaceFromData(
    dataWkid: number,
    dataElevUnit: LinearUnit,
    tinTriangles: any[],
    cgPoints: any[] | undefined = undefined,
    contours: any[] | undefined = undefined,
    ensureTin: boolean = false
  ): Promise<boolean> {
    let result: boolean = false;
    try {
      designStore.setLoader('Loading surface from data...');
      if (this.view) {
        const surface = new TinContourSurface(this.history, this.view, this.mapElevUnit, true);
        const hasData = await surface.generateSurfaceFromData(dataWkid, dataElevUnit, tinTriangles, cgPoints, contours, ensureTin);
        if (hasData) {
          try {
            designStore.setLoader('Displaying surface from data...');
            result = await surface.displaySurface(dataWkid, false, 1, ensureTin);
          } finally {
            designStore.setLoader("")
          }
        } else {
          MessageCenter.addError('No surface data received.');
        }
      } else {
        MessageCenter.addError('Map view does not currently exist.');
      }
    } finally {
      designStore.setLoader('');
    }

    return result;
  }

  public static async displayOriginalSurface(waitForScene: boolean = false): Promise<boolean> {
    let result: boolean = false;
    if (this.instance) {
      result = await this.instance._displayOriginalSurface(waitForScene);
    } else {
      MessageCenter.addError('No DesignContainerVM instance found.');
    }
    return result;
  }

  private async _displayOriginalSurface(waitForScene: boolean): Promise<boolean> {
    let result: boolean = false;
    if (waitForScene) {
      try {
        designStore.setLoader('Displaying original surface...');
      } finally {
        designStore.setLoader('');
      }

    }
    if (this.originalSurface) {
      result = await this.originalSurface.displaySurface(this.sourceWkid, waitForScene);
    }
    return result;
  }

  public static async saveStoredGpSessionIdToResources(storedGpSessionId: string): Promise<void> {
    const sessionUuid = designStore.projectsStore.currentSessionId;

    const currentUser = UserIdentity.username;
    // console.log('Current user: ' + currentUser);

    const newResource = {
      sessionResourceTypeId: DesignContainerVM.origGpSurfaceTypeId,
      sessionResourceValue: storedGpSessionId,
    };

    const requestData = {
      currentUser: currentUser,
      sessionUuid: sessionUuid,
      sessionResources: [newResource],
    };

    await PRJAPI.EftSessionController.addSessionResourceUsingPOST(requestData);
  }

  public static async updateStoredGpSessionIdInResources(
    storedGpSessionId: string,
    newGpSessionId: string
  ): Promise<void> {
    const sessionUuid = designStore.projectsStore.currentSessionId;
    const currentUser = UserIdentity.username; // localStorage['username'];
    // console.log('Current user: ' + currentUser);

    let failedToUpdate = true;
    try {
      const response = await PRJAPI.EftSessionController.getSessionResourcesUsingGET(
        sessionUuid,
        DesignContainerVM.origGpSurfaceTypeId
      );

      if (response.data) {
        // console.warn(response.data);
        if (0 < response.data.length) {
          const resource = response.data.find((item) => {
            return item.sessionResourceValue === storedGpSessionId;
          });

          if (resource) {
            const requestData = {
              currentUser: currentUser,
              sessionResourceID: resource.sessionResourceId,
              sessionResourceValue: newGpSessionId,
            };

            await PRJAPI.EftSessionController.updateSessionResourceUsingPOST(requestData);
            failedToUpdate = false;
          }
        }
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      if (failedToUpdate) {
        console.error('Failed to update stored GP session ID from ' + storedGpSessionId + ' to ' + newGpSessionId);
      }
    }
  }

  @action.bound removeBaseStructureGraphics() {
    const layer = this.view!.map.findLayerById('baseStructureGraphics') as GraphicsLayer;
    if (layer) {
      layer.graphics.removeAll();
    }
  }

  @action.bound async getDEMMetadata() {
    let result = false;
    if (!designStore?.projectsStore?.currentProject?.projectUuid) return;
    try {
      const response: any = await SETAPI.DesignElementController.getDEMMetadataByProjectUuidUsingPOST(
        designStore.projectsStore.currentProject.projectUuid
      );
      if (response && response.data && response.data.projectUuid != null && response.data.demSurfaceName != "") {
        this.dataSourceType = response.data.dataSourceType;
        this.metadata = response.data.metadata;
        result = true;
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
    return result;
  }

  @action.bound async applyPadStructuresToMap() {
    // clear all design element layers
    if (this.view) {
      removeLayers(padDesignStore.padLayers, this.view);
    }
    if (padDesignStore.selectedPadsInfo.length > 0 && this.view) {
      const ids = padDesignStore.selectedPadsInfo.map((a) => a.designElementId);
      // retrieve the JSON for these structures
      const response: AxiosResponse<
        DesignElementResponse[]
      > = await SETAPI.DesignElementController.surveyDesignElementByIdUsingGET(ids.join(','));
      for (const designElement of response['data']) {
        if (designElement?.json) {
          if (designElement.designElementTypeId === 14) {
            //Pad
            const pad: any = JSON.parse(designElement?.json);
            padDesignStore.addPadToMap(pad, this.view, false, projectsLibraryStore.projectSpatialReference!, designElement.designElementId!.toString());
          } else {
            console.error('applyStructuresToMap:pads', 'Unrecognized/unsupported designElementTypeId.');
          }
          turnOffLabelLayer(this.view);
        }
      }
    }
    if (this.view && !designStore.hideSingleCrossSection) await designStore.validateSingleCrossSection(this.view);
  }
  @action.bound async applyPitStructuresToMap() {
    // console.warn('applyPitStructuresToMap: pitDesignStore.selectedPitsInfo =', pitDesignStore.selectedPitsInfo);
    // clear all design element layers
    if (this.view) {
      removeLayers(pitDesignStore.pitLayers, this.view);
      removeLayers(padDesignStore.padLayers, this.view);
      removeLayers(pitDesignStore.dikedPitLayers, this.view);
    }

    if (pitDesignStore.selectedPitsInfo.length) {
      const ids = pitDesignStore.selectedPitsInfo.map((a) => a.designElementId);
      // retrieve the JSON for these structures
      const response: AxiosResponse<
        DesignElementResponse[]
      > = await SETAPI.DesignElementController.surveyDesignElementByIdUsingGET(ids.join(','));
      // console.log(response);
      for (const designElement of response['data']) {
        if (designElement?.json && this.view) {
          switch (designElement.designElementTypeId) {
            case 11:
            case 12:
            case 13: {
              //Pit-Rectangular
              const pit: any = JSON.parse(designElement?.json);
              pitDesignStore.addPitToMap(pit, this.view, false, projectsLibraryStore.projectSpatialReference!, designElement.designElementId!.toString());
              break;
            }
            default:
              console.error('applyStructuresToMap:pits', 'Unrecognized/unsupported designElementTypeId.');
              // console.log('designElement id : ', designElement.designElementId);
              break;
          }
          turnOffLabelLayer(this.view);
        }
      }
    }
    if (this.view && !designStore.hideSingleCrossSection) await designStore.validateSingleCrossSection(this.view);
  }

  @action.bound redefineSketch() {
    this.drawingActive = false;
    pitDesignStore.chooseCenter = false;
    padDesignStore.chooseCenter = false;
    const sketch = new Sketch({
      view: this.view,
      layer: this.centerPointLayer,
      creationMode: 'single',
      snappingOptions: {
        distance: 21,
        enabled: designStore.snappingEnabled,
      },
      visibleElements: {
        createTools: {
          point: false,
          polyline: false,
          polygon: false,
          rectangle: false,
          circle: false,
        },
        selectionTools: {
          'rectangle-selection': false,
          'lasso-selection': false,
        },
        settingsMenu: false,
        undoRedoMenu: false,
      } as any,
      defaultUpdateOptions: {
        enableScaling: false,
        enableRotation: false,
        toggleToolOnClick: false,
        preserveAspectRatio: false,
      }
    });

    this.centerPointSketch?.destroy();
    this.centerPointSketch = sketch;

    this.centerPointSketch?.on('create', async (event) => {
      if (event.state === 'complete') {
        if (this._snapTooltipProvider) {
          this._snapTooltipProvider.stop();
          this._snapTooltipProvider = undefined;
        }
        const point = event.graphic.geometry as Point;
        const projectedPoint: any = esriProjection.project(point, projectsLibraryStore.projectSpatialReference!);
        await this.getCenterPoint(projectedPoint);
        this.redefineSketch();
      } else if (event.state === "cancel") {
        if (this._snapTooltipProvider) {
          this._snapTooltipProvider.stop();
          this._snapTooltipProvider = undefined;
        }
        this.redefineSketch();
        pitDesignStore.chooseCenter = false;
        padDesignStore.chooseCenter = false;
      }
    });
    this.centerPointSketch?.on('update', async (event) => {
      if (event.state === 'start') {
        pitDesignStore.chooseCenter = true;
        padDesignStore.chooseCenter = true;
        if (this._snapTooltipProvider && designStore.currentSurface.surfaceDefClassName === 'survey' && designStore.snappingEnabled) this._snapTooltipProvider.start();
      }

      if (event.state === 'complete') {
        if (this._snapTooltipProvider) {
          this._snapTooltipProvider.stop();
          this._snapTooltipProvider = undefined;
        }
        const point = event.graphics[0].geometry as Point;
        const projectedPoint: any = esriProjection.project(point, projectsLibraryStore.projectSpatialReference!);
        await this.getCenterPoint(projectedPoint);
        this.redefineSketch();
        pitDesignStore.chooseCenter = false;
        padDesignStore.chooseCenter = false;
      }
    });

  }

  @action.bound chooseCenterPoint() {
    if (this.drawingActive === true) {
      this.redefineSketch();
    } else {
      if (pitDesignStore.chooseCenter || padDesignStore.chooseCenter) {
        this.drawingActive = true;
        if (designStore.currentSurface.surfaceDefClassName === 'survey') {
          if (!this.surveyPointsLayer) this.surveyPointsLayer = this.view?.map.findLayerById(AppConstants.SurveyPointsLayerId) as GraphicsLayer;
          this._snapTooltipProvider = new SnapTooltipProvider(this.view, this.surveyPointsLayer, projectsLibraryStore.projectSpatialReference!);
          this._snapTooltipProvider.getSurveyPoints(this.surveyPointsLayer);
          const fsls = new FeatureSnappingLayerSource({ enabled: true, layer: this.surveyPointsLayer } as any);
          this.centerPointSketch?.snappingOptions.featureSources.push(fsls);
          this.centerPointSketch?.create("point");
          if (designStore.snappingEnabled) this._snapTooltipProvider?.start();
        } else {
          this.centerPointSketch?.create("point");
        }
      }
    }
  }

  @action.bound toggleSurveyPointLayer() {
    const surveyPointsLayer = this.view?.map.findLayerById(AppConstants.SurveyPointsLayerId);
    if (surveyPointsLayer) {
      if (!designStore.snappingEnabled) surveyPointsLayer.visible = false;
      else surveyPointsLayer.visible = true;
    }
  }

  @action.bound toggleSnapping() {
    if (!designStore.snappingEnabled) {
      if (this.centerPointSketch) this.centerPointSketch.snappingOptions.enabled = false;
      if (this._snapTooltipProvider) {
        this._snapTooltipProvider.stop();
        this._snapTooltipProvider = undefined;
      }
    } else {
      this.centerPointSketch!.snappingOptions.enabled = true;
      if (this.centerPointSketch?.state === "active") this._snapTooltipProvider?.start();
    }
  }

  async getCenterPoint(point) {
    // Pit Choose Center
    if (pitDesignStore && pitDesignStore.chooseCenter) {
      const { newDesignForm } = pitDesignStore;
      pitDesignStore.newDesignForm.center.x = point.x;
      pitDesignStore.newDesignForm.center.y = point.y;

      const postJSON: IpostGetElevationData = {
        basePoint: {
          x: pitDesignStore.newDesignForm.center.x,
          y: pitDesignStore.newDesignForm.center.y,
        },
        designElementId: designStore.currentSurface.designElementId,
      };
      try {
        const response = await (await SETAPI.StructureController.getElevationUsingPOST(postJSON)).data;
        if (response && response.designElementId && response.basePoint) {
          if (newDesignForm.center.z === undefined || newDesignForm.center.z === 0) {
            newDesignForm.center.z = +response.basePoint.z!.toFixed(3);
          }

          if (newDesignForm.type === 'Diked Pit') {
            newDesignForm.bottomElevation = +response.basePoint.z!.toFixed(3);
            newDesignForm.topElevation = newDesignForm.bottomElevation + newDesignForm.pitDepth;
            newDesignForm.pitDepth = newDesignForm.topElevation - newDesignForm.bottomElevation;
          }
          if (newDesignForm.type === 'Round Pit') {
            if (newDesignForm.radius > 0) {
              this.drawCirclePit(pitDesignStore.newDesignForm);
            }
          } else {
            this.drawRectangularPit(pitDesignStore.newDesignForm);
          }
        }

        this.addCenterMarker(point);
        pitDesignStore.clearNotification();
      } catch (error) {
        pitDesignStore.newDesignForm.center.x = 0;
        pitDesignStore.newDesignForm.center.y = 0;
        this.removeCenterMarker();
        if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
          // pitDesignStore.showNotification((error as any).response.data.errorMessage, 'Error');
          MessageCenter.addError((error as any).response.data.errorMessage);
        } else {
          // pitDesignStore.showNotification((error as any).message, 'Error');
          MessageCenter.addError((error as any).message);
        }
      }
      pitDesignStore.chooseCenter = false;
    }
    // Pad Choose Center
    if (padDesignStore && padDesignStore.chooseCenter) {
      padDesignStore.newDesignForm.center.x = parseInt(point.x);
      padDesignStore.newDesignForm.center.y = parseInt(point.y);

      const postJSON: IpostGetElevationData = {
        basePoint: {
          x: padDesignStore.newDesignForm.center.x,
          y: padDesignStore.newDesignForm.center.y,
        },
        designElementId: designStore.currentSurface.designElementId,
      };
      try {
        const response = await (await SETAPI.StructureController.getElevationUsingPOST(postJSON)).data;
        if (response && response.designElementId && response.basePoint) {
          padDesignStore.newDesignForm.center.z = +response.basePoint.z!.toFixed(3);
          this.drawRectangularPit(padDesignStore.newDesignForm);
        }

        this.addCenterMarker(point);
        padDesignStore.clearNotification();
      } catch (error) {
        padDesignStore.newDesignForm.center.x = 0;
        padDesignStore.newDesignForm.center.y = 0;
        this.removeCenterMarker();
        if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
          // padDesignStore.showNotification(errorResponse.errorMessage, 'Error');
          MessageCenter.addError((error as any).response.data.errorMessage);
        } else {
          // padDesignStore.showNotification((error as any).message, 'Error');
          MessageCenter.addError((error as any).message);
        }
      }
      padDesignStore.chooseCenter = false;
    }
  }

  @action.bound removeCenterMarker() {
    this.centerPointLayer.graphics.removeAll();
  }

  addCenterMarker(point) {
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 10,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });
    const centerPoint = new Point({
      spatialReference: projectsLibraryStore.projectSpatialReference,
      hasZ: false,
      x: point.x,
      y: point.y,
    });

    const centerPointGraphic = new Graphic({
      geometry: centerPoint,
      symbol: markerSymbol,
      attributes: {
        ObjectID: 'center-marker',
        pointName: 'center-marker',
        y: point.y,
        x: point.x,
        description: 'Marker for choose center tool.',
      },
    })

    this.removeCenterMarker();
    this.centerPointLayer.graphics.add(centerPointGraphic);
  }

  // !!! TODO: this logic doesn't make a lot of sense, should refactor and/or recomment description
  @action.bound async initializeDesignFlow() {
    // designStore.isLoading = true;
    // TODO: better way to define projectId here via useParams()
    // const pathname = window.location.pathname;
    // const routeProjectId = pathname.split('/').reverse()[0];
    const currentProject = designStore.projectsStore.currentProject;
    MessageCenter.fillMessageCacheByOriginalCodes(this.pendingEditErrorMessages);
    if (
      this.routeProjectId &&
      this.routeProjectId.length === 36 &&
      this.routeProjectId !== currentProject.projectUuid
      // !currentProject.projectUuid &&
      // !designStore.currentSurface.surfaceDefId
    ) {
      // found proj in url so get project info and set current project
      // console.log('found proj in url so get project info and set current project');
      currentProject.projectUuid = this.routeProjectId;
      designStore.projectsStore.currentProjectId = this.routeProjectId;
      await this.getAndSetCurrentProject(this.routeProjectId);
      await this.restoreProjectState();
    } else {
      // normal design project flow
      if (currentProject.projectUuid) {
        // has prject in mem so no need to fetch again
        // console.log('project in mem so no need to fetch again');
        await this.restoreProjectState();
      } else {
        // console.log('no project so create untitled one', pathname);
        // we want to wait to do this until some user input requires a project
        // await this.createInvisibleProject();
        // console.log('*** NO ProjectId in URL or Memory, show warning ***');
        const buttons = [
          {
            text: 'Go Create a Project',
            callback: () => {
              this.history.push('/projects/new-project');
              MessageCenter.clearMessages();
            },
          },
        ];
        if (userProfileStore.applicationFlags.enabledesign) {
          MessageCenter.addWarningFromCode('Project_04', buttons);
        }// Please create, or load a project before continuing.
      }
    }
    //To get Projection information in case of load direct url
    //if (currentProject.projectUuid !== undefined && projectsLibraryStore.projectProjectionInfo === '') await getSpatialRefByProject(currentProject.projectUuid)
    if (currentProject.projectUuid !== undefined && currentProject.projectUuid !== '' && projectsLibraryStore.projectProjectionInfo === '') await getSpatialRefByProject(currentProject.projectUuid)
    // designStore.isLoading = false;
  }

  @action.bound async restoreProjectState() {
    const currentProject = designStore.projectsStore.currentProject;
    if (currentProject.sessionList.length) {
      const currentSessionUuid = designStore.projectsStore.currentProject.sessionList[0].sessionUuid;
      if (currentSessionUuid) {
        // project & session created. And session associated with surface.
        await this.getSurfaceBySession(currentSessionUuid, currentProject);
      } else {
        MessageCenter.addError('No session found here, so no surface.');
      }
    }
  }
  // get surface details based on sessionUuid
  // triggers useEffect in SurveySummary.tsx with designStore.currentSurface.surfaceDefId
  // can instead pass this as a prop to this component and watch that with a use effect if needbe
  @action.bound async getSurfaceBySession(sessionUuid, currentProject) {
    try {
      const response: any = await SETAPI.SurfaceDefController.getSurfaceDefBySessionUsingGET(sessionUuid)

      let surfaceType = 'Surface';  // Default surface type

      if (response.data.length) {
        designStore.currentSurface.surfaceDefId = response.data[0].surfaceDefId;
        designStore.currentSurface.surfaceDefTitle = response.data[0].surfaceDefTitle;
        designStore.currentSurface.surfaceDefClassId = response.data[0].surfaceDefClassId;
        designStore.currentSurface.surfaceDefClassName = response.data[0].surfaceDefClassName;

        // Check the class id and change surface type if needed
        if (response.data[0].surfaceDefClassId === 2) {
          surfaceType = 'Surface-Dem';
        }

      } else {
        MessageCenter.addError('No surface found.');
      }

      // Call the API method with the determined surface type
      const response2 = await SETAPI.DesignElementController.getAllDesignElementsBySessionAndTypeUsingGET(
        sessionUuid,
        surfaceType,
        true
      );
      const surfaceDesignElements = response2['data'];
      if (surfaceDesignElements.length) {
        designStore.currentSurface.designElementId = surfaceDesignElements[0].designElementId!;
        if (typeof surfaceDesignElements[0]?.json === 'string') {
          try {
            designStore.currentSurface.borderVector = JSON.parse(surfaceDesignElements[0].json!).borderVector
          } catch (error) {
          }
        }
        if (surfaceDesignElements.length > 1) {
          console.error('More than one Surface found for this session, this is unexpected and needs to be handled');
        }
      } else {
        console.warn('No surface design element found. response2 = ', response2);
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }
  @action.bound async getAndSetCurrentProject(routeProjectId) {
    try {
      const rspns: any = await PRJAPI.EftProjectController.getProjectByIdUsingGET(routeProjectId);
      const response = rspns.data;
      if (response && response.projectUuid) {
        designStore.projectsStore.currentProject = response;
        designStore.projectsStore.currentProjectId = response.projectUuid;
        // try to find and set current session and assume session 1
        if (designStore.projectsStore.currentProject.sessionList.length) {
          designStore.projectsStore.currentSessionId =
            designStore.projectsStore.currentProject.sessionList[0].sessionUuid;
        }
      } else if (response && response.errorMessage) {
        console.warn('Get project failed, so create invisible and move on.');
        // long term we want to wait to create an invisible project
        await this.createInvisibleProject();
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  // !!!!!!!!!! DEPRECATED - NOT IN USE or INACCESSIBLE !!!!!!!!!!!!
  @action.bound async createInvisibleProject() {
    // construct projct name from user and timestamp
    let userName = stores.AppStore.userProfileStore.userName;
    if (userName === '' && localStorage.username) {
      userName = localStorage.username;
      stores.AppStore.userProfileStore.userName = userName;
    }

    const project = {
      currentUser: userName,
      projectTitle: `Untitled Project by ${userName} -${Date.now()}`,
      projectClassId: 1, // projectClassId 1 is for design
    };

    // Send Post Request
    const postJSON: ICreateProjectPOST = project;
    try {
      const response = await PRJAPI.EftProjectController.createProjectUsingPOST(postJSON);
      const data = (response.data as unknown) as ProjectDataObject;

      if (data.projectUuid) {
        // update proj id in url
        designStore.projectsStore.currentProject = data;
        // replace path to include new project id
        const splitPath = window.location.pathname.split('/');
        let newPathName = '/' + splitPath[1];
        if (splitPath[2]) newPathName = newPathName + '/' + splitPath[2];
        this.history.replace(`${newPathName}/${designStore.projectsStore.currentProject.projectUuid}`);
      } else if (data.empty === false) {
        // handle error case here until catch is working
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound async deleteProject() {
    const projectID = designStore.projectsStore.currentProject.projectUuid;
    try {
      await PRJAPI.EftProjectController.softDeleteProjectUsingDELETE(projectID);
      // unload project by resetting the project store
      designStore.projectsStore.reset();
      MessageCenter.clearMessages();
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound drawRectangularPit(designForm) {
    drawRectangularBase(this.view!, designForm, projectsLibraryStore.projectSpatialReference!);
  }
  @action.bound updateRectanglePit(designForm) {
    updateRectangularGraphic(this.view!, designForm, projectsLibraryStore.projectSpatialReference!);
  }
  @action.bound drawCirclePit(designForm) {
    drawCircleBase(this.view!, designForm, projectsLibraryStore.projectSpatialReference!);
  }
  @action.bound updateCirclePit(designForm) {
    updateRectangularGraphic(this.view!, designForm, projectsLibraryStore.projectSpatialReference!);
  }
  /**
   *
   * @param designForm
   * @param round True if drawing a round structure, false if rectangular
   */
  @action.bound drawBaseGraphic(designForm, round) {
    if (round) {
      drawCircleBase(this.view!, designForm, projectsLibraryStore.projectSpatialReference!);
    } else {
      drawRectangularBase(this.view!, designForm, projectsLibraryStore.projectSpatialReference!);
    }
  }
  /**
   *
   * @param designForm
   * @param round True if updating a round structure, false if rectangular
   */
  @action.bound updateBaseGraphic(designForm, round) {
    if (round) {
      this.updateCirclePit(designForm);
    } else {
      this.updateRectanglePit(designForm);
    }
    this.removeCenterMarker();
  }
}

export const getElevation = async (x, y) => {
  try {
    const projectUuid = designStore.projectsStore.currentProject.projectUuid;
    const payload = {
      projectUUID: projectUuid,
      x: x,
      y: y
    }
    const response = await SETAPI.SurfaceController.getElevationUsingPOST1(payload);

    if (response) {
      return response.data.z;
    }
  } catch (error) {
    return;
  }
}

export const getElevationUsingContours = async (dataPoint, mapPoint, mapView) => {
  let z: any;

  const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
  const x = dataPoint.x;
  const y = dataPoint.y;

  const pointInsidePolygon = await isPointInsidePolygon(x, y, bPolygon, projectsLibraryStore.projectSpatialReference!);

  if (pointInsidePolygon) {
    const designedContours = mapView.map.findLayerById(AppConstants.DesignedContourLayerId);
    const originalContours = mapView.map.findLayerById(AppConstants.OriginalContourLayerId);
    let contoursToUse = originalContours;
    if (designedContours) contoursToUse = designedContours;

    z = dataPoint.z ? dataPoint.z : await queryContours(contoursToUse, mapPoint, mapView);

  } else {
    z = 0;
  }
  return z;
}

const queryContours = (contoursToUse, mapPoint, mapView) => {
  let z: any;

  const features = contoursToUse.source;
  const distances: any[] = [];

  features.forEach((feature) => {
    const contourLine = feature.geometry;
    const distance = esriGeometryEngine.distance(contourLine, mapPoint, 'feet');
    const obj = { feature: feature, distance: distance, z: feature.attributes.elevation };
    distances.push(obj);
  });

  distances.sort((a, b) => {
    return a.distance - b.distance;
  });

  // Get closest contour line
  const distance1 = distances[0].distance;
  const elevation1 = distances[0].z;
  const nearestVertex = esriGeometryEngine.nearestVertex(distances[0].feature.geometry, mapPoint);
  const contourPoint1 = new Point({ x: nearestVertex.coordinate.x, y: nearestVertex.coordinate.y, spatialReference: mapView.spatialReference })
  const deltaX = nearestVertex.coordinate.x - mapPoint.x; // if negative, line is to left of point. If positive, line is to the right of point.
  const deltaY = nearestVertex.coordinate.y - mapPoint.y; // if negative, line is above point. If positive, line is below point.
  // Find next closest contour line on the other side of the point
  let contourPoint2: Point | undefined;
  let distance2;
  let elevation2;

  const remainingDistances = distances.slice(1, -1);

  for (const item of remainingDistances) {
    if (deltaX < 0) { // If line is to left of point, find the nearest one to the right (positive)
      const nearestVertex = esriGeometryEngine.nearestVertex(item.feature.geometry, mapPoint);
      const itemX = nearestVertex.coordinate.x;
      const newDeltaX = itemX - mapPoint.x;
      if (newDeltaX > 0) {
        distance2 = item.distance;
        elevation2 = item.z;
        contourPoint2 = new Point({ x: nearestVertex.coordinate.x, y: nearestVertex.coordinate.y, spatialReference: mapView.spatialReference });
        break;
      }
    } else if (deltaX > 0) {
      const nearestVertex = esriGeometryEngine.nearestVertex(item.feature.geometry, mapPoint);
      const itemX = nearestVertex.coordinate.x;
      const newDeltaX = itemX - mapPoint.x;
      if (newDeltaX < 0) {
        distance2 = item.distance;
        elevation2 = item.z;
        contourPoint2 = new Point({ x: nearestVertex.coordinate.x, y: nearestVertex.coordinate.y, spatialReference: mapView.spatialReference });
        break;
      }
    } else if (deltaY < 0) { // if contour is completely horizontal
      const nearestVertex = esriGeometryEngine.nearestVertex(item.feature.geometry, mapPoint);
      const itemY = nearestVertex.coordinate.y;
      const newDeltaY = itemY - mapPoint.y;
      if (newDeltaY > 0) {
        distance2 = item.distance;
        elevation2 = item.z;
        contourPoint2 = new Point({ x: nearestVertex.coordinate.x, y: nearestVertex.coordinate.y, spatialReference: mapView.spatialReference });
        break;
      }
    } else if (deltaY > 0) {
      const nearestVertex = esriGeometryEngine.nearestVertex(item.feature.geometry, mapPoint);
      const itemY = nearestVertex.coordinate.y;
      const newDeltaY = itemY - mapPoint.y;
      if (newDeltaY < 0) {
        distance2 = item.distance;
        elevation2 = item.z;
        contourPoint2 = new Point({ x: nearestVertex.coordinate.x, y: nearestVertex.coordinate.y, spatialReference: mapView.spatialReference });
        break;
      }
    }
  }

  let zB;
  let bA;
  let zC;
  let bC;
  if (contourPoint1 && contourPoint2) {
    bC = esriGeometryEngine.distance(contourPoint1, contourPoint2, 'feet');

    if (elevation1 > elevation2) {
      zB = elevation2;
      zC = elevation1;
      bA = distance2;
    } else if (elevation2 > elevation1) {
      zB = elevation1;
      zC = elevation2;
      bA = distance1;
    }
    else if (elevation1 === elevation2) {
      z = elevation1;
    }
  }

  if (zB && bA && zC && bC) {
    z = ((zC - zB) / bC) * bA + zB;
  }

  return z;
}